/**
 * Weapon-FX prefab components.
 *
 * Each prefab is a tiny R3F group that lives for a fixed lifetime then
 * unmounts. The mathematical model is parameterized particle systems
 * styled to read voxel — cube particles, emissive palette, no
 * pre-authored frames. The plan reserves animated VOX sequences for
 * the moments that deserve choreography (kill effects, deployment
 * anims).
 *
 * Color sourced from `REFERENCE_OLIVE.emissive` so the FX read
 * coherent with the rest of the cosmetic palette. Damage-type tints
 * can override (energy → cyan-ish, ballistic → warm orange).
 *
 * Plan reference: docs/dev/visual-engine/voxel-cosmetics-plan.md §1
 * (weapon FX exception) + §4 W4.
 */

import { useMemo, useRef } from "react";
import {
  type Group,
  type Mesh,
  Matrix4,
  Quaternion,
  Vector3,
  type InstancedMesh,
  type Material,
  type Color,
} from "three";
import { useFrame } from "@react-three/fiber";

import { SeededRng } from "../../determinism/cosmeticSeed";
import type { Damage } from "../../replay/events";
import type { WeaponFxSpawn, World3 } from "./types";

const PALETTE: Record<Damage, { primary: string; emissive: string }> = {
  ballistic: { primary: "#ff6a1c", emissive: "#ffb04a" },
  energy: { primary: "#22e0ff", emissive: "#8fefff" },
  missile: { primary: "#ff3322", emissive: "#ffcc66" },
  explosive: { primary: "#ff6a1c", emissive: "#ffd25a" },
  other: { primary: "#ff6a1c", emissive: "#ffd25a" },
};

/** Miss palette — desaturated grey so the eye reads "shot did not
 *  connect" without needing a separate prefab geometry. */
const MISS_PALETTE = { primary: "#7a8090", emissive: "#b8c0d0" };

function colorFor(fx: { damageType: Damage; miss?: boolean }): {
  primary: string;
  emissive: string;
} {
  if (fx.miss) return MISS_PALETTE;
  return PALETTE[fx.damageType] ?? PALETTE.other;
}

function lifeProgress(fx: WeaponFxSpawn & { spawnedAtMs: number; expiresAtMs: number }): number {
  const now = performance.now();
  const denom = Math.max(1, fx.expiresAtMs - fx.spawnedAtMs);
  return Math.max(0, Math.min(1, (now - fx.spawnedAtMs) / denom));
}

interface PrefabProps {
  fx: WeaponFxSpawn & { spawnedAtMs: number; expiresAtMs: number };
}

// ── Ballistic tracer ──────────────────────────────────────────────────────

/** A short chain of bright cubes streaking from origin toward target. */
export function BallisticTracer({ fx }: PrefabProps) {
  const color = colorFor(fx);
  const groupRef = useRef<Group>(null);

  const path = useMemo(() => {
    const start = new Vector3(...fx.origin);
    const end = new Vector3(...fx.target);
    return { start, end };
  }, [fx.origin, fx.target]);

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const t = lifeProgress(fx);
    const pos = path.start.clone().lerp(path.end, t);
    g.position.copy(pos);
    const mat = (g.children[0] as Mesh | undefined)?.material as Material & { opacity?: number; transparent?: boolean };
    if (mat) {
      mat.transparent = true;
      mat.opacity = 1 - t;
    }
  });

  const dir = useMemo(() => path.end.clone().sub(path.start).normalize(), [path]);
  const quat = useMemo(() => new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), dir), [dir]);

  return (
    <group ref={groupRef} quaternion={quat}>
      <mesh>
        <boxGeometry args={[0.05, 0.05, 0.4]} />
        <meshStandardMaterial
          color={color.primary}
          emissive={color.emissive}
          emissiveIntensity={1.8}
        />
      </mesh>
    </group>
  );
}

// ── Energy beam ───────────────────────────────────────────────────────────

/** A thick emissive cylinder held for ~400ms with a fade tail. */
export function EnergyBeam({ fx }: PrefabProps) {
  const color = colorFor(fx);
  const ref = useRef<Mesh>(null);

  const { center, length, quat } = useMemo(() => {
    const a = new Vector3(...fx.origin);
    const b = new Vector3(...fx.target);
    const c = a.clone().add(b).multiplyScalar(0.5);
    const d = b.clone().sub(a);
    const len = d.length();
    d.normalize();
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), d);
    return { center: c, length: len, quat: q };
  }, [fx.origin, fx.target]);

  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const t = lifeProgress(fx);
    const mat = m.material as Material & { opacity?: number; transparent?: boolean; emissiveIntensity?: number };
    mat.transparent = true;
    mat.opacity = 1 - t * 0.85;
    if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = 2.2 * (1 - t);
  });

  return (
    <mesh ref={ref} position={center} quaternion={quat}>
      <cylinderGeometry args={[0.04, 0.04, length, 8]} />
      <meshStandardMaterial
        color={color.primary}
        emissive={color.emissive}
        emissiveIntensity={2.2}
      />
    </mesh>
  );
}

// ── Missile arc ───────────────────────────────────────────────────────────

/** Bezier-arced path with a short cube smoke trail. */
export function MissileArc({ fx }: PrefabProps) {
  const color = colorFor(fx);
  const headRef = useRef<Mesh>(null);
  const trailRef = useRef<InstancedMesh>(null);
  const trailLen = 12;

  const { start, end, mid } = useMemo(() => {
    const s = new Vector3(...fx.origin);
    const e = new Vector3(...fx.target);
    // Apex height grows with distance — basic ballistic feel.
    const dist = s.distanceTo(e);
    const m = s.clone().add(e).multiplyScalar(0.5);
    m.y += Math.max(0.4, dist * 0.3);
    return { start: s, end: e, mid: m };
  }, [fx.origin, fx.target]);

  const evalBezier = (t: number, out: Vector3) => {
    const oneMt = 1 - t;
    out.set(
      oneMt * oneMt * start.x + 2 * oneMt * t * mid.x + t * t * end.x,
      oneMt * oneMt * start.y + 2 * oneMt * t * mid.y + t * t * end.y,
      oneMt * oneMt * start.z + 2 * oneMt * t * mid.z + t * t * end.z,
    );
  };

  useFrame(() => {
    const t = lifeProgress(fx);
    const head = headRef.current;
    if (head) {
      const p = new Vector3();
      evalBezier(t, p);
      head.position.copy(p);
    }
    const trail = trailRef.current;
    if (trail) {
      const m = new Matrix4();
      const p = new Vector3();
      const window = 0.18; // trail covers ~18% of the arc behind the head
      for (let i = 0; i < trailLen; i++) {
        const offset = (i / (trailLen - 1)) * window;
        const ti = Math.max(0, t - offset);
        evalBezier(ti, p);
        m.makeTranslation(p.x, p.y, p.z);
        trail.setMatrixAt(i, m);
      }
      trail.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group>
      <mesh ref={headRef}>
        <boxGeometry args={[0.08, 0.08, 0.08]} />
        <meshStandardMaterial
          color={color.primary}
          emissive={color.emissive}
          emissiveIntensity={1.8}
        />
      </mesh>
      <instancedMesh ref={trailRef} args={[undefined, undefined, trailLen]}>
        <boxGeometry args={[0.04, 0.04, 0.04]} />
        <meshStandardMaterial
          color={"#666666"}
          emissive={color.emissive}
          emissiveIntensity={0.4}
          transparent
          opacity={0.5}
        />
      </instancedMesh>
    </group>
  );
}

// ── Explosion burst ───────────────────────────────────────────────────────

/** Expanding sphere of palette-tinted particles. */
export function ExplosionBurst({ fx }: PrefabProps) {
  const color = colorFor(fx);
  const groupRef = useRef<InstancedMesh>(null);
  const particleCount = 24;

  const directions = useMemo(() => {
    const rng = new SeededRng(fx.seed ^ fx.id);
    const out: World3[] = [];
    for (let i = 0; i < particleCount; i++) {
      // Fibonacci-sphere distribution jittered by the seed.
      const u = rng.next();
      const v = rng.next();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      out.push([
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta),
      ]);
    }
    return out;
  }, [fx.seed, fx.id]);

  useFrame(() => {
    const m = groupRef.current;
    if (!m) return;
    const t = lifeProgress(fx);
    const matrix = new Matrix4();
    const radius = 0.6 * t;
    for (let i = 0; i < particleCount; i++) {
      const d = directions[i];
      matrix.makeTranslation(
        fx.origin[0] + d[0] * radius,
        fx.origin[1] + d[1] * radius,
        fx.origin[2] + d[2] * radius,
      );
      m.setMatrixAt(i, matrix);
    }
    m.instanceMatrix.needsUpdate = true;
    const mat = m.material as Material & {
      opacity?: number;
      transparent?: boolean;
      emissiveIntensity?: number;
      color?: Color;
    };
    mat.transparent = true;
    mat.opacity = 1 - t;
    if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = 1.8 * (1 - t * 0.5);
  });

  return (
    <instancedMesh ref={groupRef} args={[undefined, undefined, particleCount]}>
      <boxGeometry args={[0.08, 0.08, 0.08]} />
      <meshStandardMaterial
        color={color.primary}
        emissive={color.emissive}
        emissiveIntensity={1.8}
      />
    </instancedMesh>
  );
}

// ── Muzzle flash ──────────────────────────────────────────────────────────

/** Tiny burst at the firing origin. */
export function MuzzleFlash({ fx }: PrefabProps) {
  const color = colorFor(fx);
  const ref = useRef<Mesh>(null);

  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const t = lifeProgress(fx);
    const scale = 1 - t;
    m.scale.set(scale, scale, scale);
    const mat = m.material as Material & { opacity?: number; transparent?: boolean };
    mat.transparent = true;
    mat.opacity = 1 - t;
  });

  return (
    <mesh ref={ref} position={fx.origin}>
      <boxGeometry args={[0.18, 0.18, 0.18]} />
      <meshStandardMaterial
        color={color.primary}
        emissive={color.emissive}
        emissiveIntensity={2.6}
      />
    </mesh>
  );
}

// ── Shockwave ring (B2) ──────────────────────────────────────────────────
//
// Expanding billboard torus that rides the explosion / kill moment.
// We use a thin torus (large major radius growing over life) so the
// player reads it as a ground-level shock ring, not a dome. The
// emissive ramps in then fades; the radius eases out with a square
// root so the early frames push fast and the tail decelerates.

const SHOCKWAVE_MAX_RADIUS = 1.6;
const SHOCKWAVE_TUBE_RADIUS = 0.04;

export function ShockwaveRing({ fx }: PrefabProps) {
  const ref = useRef<Mesh>(null);

  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const t = lifeProgress(fx);
    // Square-root ease — fast at first, decelerates toward the edge.
    const r = SHOCKWAVE_MAX_RADIUS * Math.sqrt(t);
    // Scale the torus uniformly in XZ; flat in Y because torus is
    // authored on the XY plane and we rotated -π/2 X to lay it flat.
    m.scale.set(r, r, r);
    const mat = m.material as Material & {
      opacity?: number;
      transparent?: boolean;
      emissiveIntensity?: number;
    };
    mat.transparent = true;
    // Ramp in fast, fade out slowly — peak around t≈0.18.
    const fadeIn = Math.min(1, t / 0.18);
    const fadeOut = 1 - Math.max(0, (t - 0.18) / 0.82);
    mat.opacity = fadeIn * fadeOut;
    if (mat.emissiveIntensity !== undefined) {
      mat.emissiveIntensity = 2.4 * fadeIn * fadeOut;
    }
  });

  const color = colorFor(fx);
  return (
    <mesh
      ref={ref}
      position={[fx.origin[0], fx.origin[1] - 0.4, fx.origin[2]]}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <torusGeometry args={[1, SHOCKWAVE_TUBE_RADIUS, 8, 64]} />
      <meshStandardMaterial
        color={color.primary}
        emissive={color.emissive}
        emissiveIntensity={2.4}
        transparent
        opacity={0}
      />
    </mesh>
  );
}
